/* eslint-disable react/no-danger */

// node modules
import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useStyles from 'isomorphic-style-loader/useStyles';
import { gql, useQuery } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// app modules: actions
import consentToCookies from 'actions/user/consentToCookies';

// app modules: components
import ToastContents from 'components/ToastContents';

// app modules: hooks
import usePriceInjector from 'hooks/usePriceInjector';

// constants
import { COOKIE_CONSENT_STORAGE_KEY, COOKIE_CONSENT_TOAST_ID } from 'constants/consent';

// assets
import CssBanner from '../Banner/Banner.scss';

const GET_PROMOS = gql`
  query GetPromoBanners($displayType: DisplayType) {
    PromoBanner(displayType: $displayType) {
      id
      message
      style
    }
  }
`;

export function AcceptButton({ closeToast, type, onAccept }) {
  return (
    <button
      className={`Toastify__close-button Toastify__close-button--${type}`}
      type="button"
      aria-label="close"
      onClick={event => {
        event.stopPropagation();
        onAccept();
        if (closeToast) {
          closeToast(event);
        }
      }}
    >
      ✖
    </button>
  );
}

AcceptButton.propTypes = {
  closeToast: PropTypes.func,
  type: PropTypes.string,
  onAccept: PropTypes.func.isRequired,
};

AcceptButton.defaultProps = {
  closeToast: null,
  type: 'success',
};

function CookieConsent() {
  const containerRef = useRef(null);
  useStyles(CssBanner);
  usePriceInjector(containerRef);
  const dispatch = useDispatch();

  const displayType = useSelector(state => state.dimensions.displayType);
  const cookieConsent = useSelector(state => state.user.cookie_consent);

  const [loaded, setLoaded] = useState(false);
  const [cookieBannerShown, setCookieBannerShown] = useState(false);

  useEffect(
    () => {
      try {
        const savedCookieConsent = localStorage.getItem(COOKIE_CONSENT_STORAGE_KEY) === 'true';
        if (cookieConsent !== 'dismissed' && savedCookieConsent) {
          dispatch(consentToCookies('dismissed'));
        }
      } catch (err) {
        console.warn('Failed to read cookie consent from localstorage', err);
      }
      setLoaded(true);
    },
    [cookieConsent, dispatch],
  );

  const handleAccept = () => {
    dispatch(consentToCookies('dismissed'));
    try {
      localStorage.setItem(COOKIE_CONSENT_STORAGE_KEY, 'true');
    } catch (err) {
      console.warn('Failed to save cookie consent to localstorage', err);
    }
  };

  useQuery(GET_PROMOS, {
    variables: { _authenticated: true, displayType },
    fetchPolicy: 'cache-first',
    errorPolicy: 'all',
    ssr: false,
    skip: !loaded,
    onCompleted: ({ PromoBanner }) => {
      if (cookieConsent !== 'dismissed' && !cookieBannerShown) {
        const cookieMessage = PromoBanner.find(_banner => _banner.id === 'CookieConsent');
        if (cookieMessage?.message) {
          toast.success(
            <ToastContents>
              <div
                ref={containerRef}
                className="banner__content"
                dangerouslySetInnerHTML={{
                  __html: cookieMessage.message,
                }}
              />
            </ToastContents>,
            {
              toastId: COOKIE_CONSENT_TOAST_ID,
              position: toast.POSITION.BOTTOM_LEFT,
              hideProgressBar: true,
              autoClose: false,
              closeOnClick: false,
              draggable: false,
              closeButton: <AcceptButton onAccept={handleAccept} />,
            },
          );
          setCookieBannerShown(true);
          dispatch(consentToCookies('displayed'));
        } else {
          dispatch(consentToCookies('not_required'));
        }
      }
    },
  });

  return null;
}

export default CookieConsent;
